import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { Grid, MenuItem } from '@mui/material';
import { showModal } from 'app/slice/modal.slice';
import { imgs } from 'constants/images';
import _ from 'lodash';
import moment from 'moment';
import DropdownItem from 'react-bootstrap/esm/DropdownItem';
import { routeName } from 'routes/routes-name';

/** TRANSLATION */
import { useTranslation } from 'react-i18next';

/** UTILS && CONSTANTS */
import { numberToCurrencyFormat } from 'utils/app.utils';
import { dateTimeToView, dateToView } from 'utils/date.utils';
import { UseSnCode } from 'utils/useSnCode';
import UseWindowSize from 'utils/useWindowSize';
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils';

/** COMPONENT */
import { swalDelete } from 'component/Alert/Swal';
import ButtonCustom from 'component/Button/ButtonCustom';
import HeaderLabel from 'component/CardCustom/HeaderLabel';
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker';
import InputTextSearch from 'component/Input/InputTextSearch';
import DefaultPagination from 'component/Pagination/DefaultPagination';
import SearchPatients from 'component/SearchPatients';
import FilterSelect from 'component/Select/FilterSelect';
import TableCustom from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';
import BtnProfile from 'component/Treatments/BtnProfile';
import PopupPatientDetail from 'component/Treatments/PopupPatientDetail';
import { notiSuccess } from 'component/notifications/notifications';
import CancelLabOrder from 'features/lab-order/component/CancelLabOrder';
import FormLabOrder from 'features/lab-order/component/FormLabOrder';
import ViewLabOrder from 'features/lab-order/component/ViewLabOrder';

/** API */
import { fileUrl } from 'api/api';
import TreatmentsApi from 'api/dentists/treatment.api';
import LabOrderApi, { LabOrderListInterface } from 'api/lab-order.api';
import LabCompanyApi from 'api/setting/default-labs/lab-company.api';

/** STYLE */
import { selectMe } from 'app/slice/user.slice';
import * as UseStyled from 'features/lab-order/lab-order-style';
import * as TreatmentsUseStyled from 'features/treatments/UseStyle';

export default function LabOrderPatient() {
    const { t } = useTranslation()
    const user = useSelector(selectMe)
    const location = useLocation()
    const dispatch = useDispatch()
    const [width] = UseWindowSize()
    const permissions = { isCreate: isCreate(), isDelete: isDelete(), isUpdate: isUpdate(), isView: isView() }

    const [isShowPopupDetail, setIsShowPopupDetail] = useState<boolean>(false)
    const [patientId, setPatientId] = useState<number>(0)
    const [patient, setPatient] = useState<any>({})

    const [isViewDetail, setIsViewDetail] = useState<boolean>(false)
    const [isForm, setIsForm] = useState<boolean>(false)
    const [popupCancel, setPopupCancel] = useState<boolean>(false)
    const [labOrderId, setLabOrderId] = useState<number>(0)
    const [labOrderNumber, setLabOrderNumber] = useState<string>('')

    /** FILTER */
    const [filterSearch, setFilterSearch] = useState<string>('')
    const [filterRangeDate, setFilterRangeDate] = useState<string[]>(emptyRangePicker)
    const [filterLabCompany, setFilterLabCompany] = useState<number>(0)
    const [filterLabOrderStatus, setFilterLabOrderStatus] = useState<string>('ALL')
    const [page, setPage] = useState(1)
    useEffect(() => { setPageLimit(width >= 1441 ? 10 : 3) }, [width])
    const [pageLimit, setPageLimit] = useState(10)
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState('dateOrder')
    const [sortType, setSortType] = useState('DESC')
    const history = useHistory()
    /** OPTION */
    const [labCompanyOptions, setLabCompanyOptions] = useState<any[]>([])

    /** DATA */
    const [labOrders, setLabOrders] = useState<any[]>([])

    const [snType, setSnType] = useState(false);
    useEffect(() => {
        if (location) {
            const { state }: any = location
            loadPatient(state?.patientId)

        }
    }, [location])
    const loadData = useCallback(async (pId?: number) => {
        let condition: any = {}
        if (page) condition = { ...condition, page: page }
        if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
        if (sortBy) condition = { ...condition, sortBy: sortBy }
        if (sortType) condition = { ...condition, sortType: sortType }
        if (filterSearch) condition = { ...condition, search: filterSearch }
        if (filterLabCompany) condition = { ...condition, labCompanyId: filterLabCompany }
        if (filterLabOrderStatus && filterLabOrderStatus !== 'ALL') condition = { ...condition, labOrderStatus: filterLabOrderStatus }
        if (filterRangeDate) {
            if ((filterRangeDate[0] !== null || filterRangeDate[0] !== '') && (filterRangeDate[1] !== null || filterRangeDate[1] !== ''))
                condition = { ...condition, dateStart: filterRangeDate[0], dateEnd: filterRangeDate[1] }
        }
        const patients = await LabOrderApi.findAllByPatientId(pId || patientId, condition)
        if (patients.status === 200) {
            history.replace(window.location.pathname, {
                patientId: pId
            })

            setLabOrders(patients.data)

            setRowCount(patients.headers['x-total'])
        }


    }, [page, pageLimit, sortBy, sortType, filterSearch, filterRangeDate, filterLabCompany, filterLabOrderStatus, patientId])

    useEffect(() => { if (patientId) loadData(patientId) }, [page, pageLimit, sortBy, sortType, filterSearch, filterRangeDate, filterLabCompany, filterLabOrderStatus, patientId])
    useEffect(() => { loadOptions() }, [])



    const handleSnCode = useCallback(() => {
        const fetchData = async () => {
            const sn = await UseSnCode();
            setSnType(sn);
        };

        fetchData();

    }, [snType]);

    useEffect(() => {
        handleSnCode()
    }, []);

    const loadOptions = async () => {
        const condition: any = { page: 1, pageLimit: 50, status: 'ACTIVE' }
        await LabCompanyApi.findAll(condition).then(({ data }) => {
            setLabCompanyOptions(data)
        })
    }

    const loadPatient = async (id: number) => {
        if (id) {

            const patientData = await TreatmentsApi.findPatient(id)
            if (patientData.status === 200 && !_.isEmpty(patientData.data)) {
                setPatientId(patientData.data.patientId)
                setPatient(patientData.data)


            }
        }
    }


    const onView = (e: any, objVal: LabOrderListInterface) => {
        setLabOrderId(objVal.labOrderId)
        setIsViewDetail(true)
        dispatch(showModal())
    }

    const onClose = () => {
        setLabOrderId(0)
        setIsViewDetail(false)

    }

    const onCreate = () => {
        setIsForm(true)
        dispatch(showModal())
    }

    const onEdit = (objData: LabOrderListInterface) => {
        const { labOrderId: id } = objData
        setLabOrderId(id)

        dispatch(showModal())
        setIsForm(true)
    }

    const onDelete = (objData: LabOrderListInterface) => {
        const { labOrderId: id, labOrderNumber: lbNo } = objData
        swalDelete(
            `<p class="mb-0 mx-auto" style="max-width: 325px">${t('LAB_ORDER.ALERT.CONFIRM_DELETE')} ${t('LAB_ORDER.MESSAGE.YESNO')}/p>`,
            `<p class="text-center mb-0 mx-auto" style="max-width: 306px">${t(`LAB_ORDER.MESSAGE.WANT_TO_DEL`)} ${t(`LAB_ORDER.MESSAGE.NO_LAB`)} ${lbNo} <br /> ${t('LAB_ORDER.MESSAGE.YESNO')}</p>`,
            (res: any) => {
                if (res) {
                    LabOrderApi.remove(id)
                        .then((resp) => {
                            notiSuccess(t('LAB_ORDER.MESSAGE.SUCCESS.DELETE'))
                            loadData()
                        })
                }
            },
            { confirmButtonText: t('LAB_ORDER.ALERT.BTN_CONFIRM_DELETE') }
        )
    }

    const onCancel = (objData: LabOrderListInterface) => {
        const { labOrderId: id, labOrderNumber: lbNo } = objData
        setLabOrderId(id)
        setLabOrderNumber(lbNo)
        dispatch(showModal())
        setPopupCancel(true)
    }
    const onReset = () => {
        setLabOrderId(0)
        setIsForm(false)
    }
    const desktopHeadCells = [
        { id: 'no', disablePadding: false, label: t('#'), class: 'text-nowrap' },
        { id: 'dateOrder', disablePadding: false, label: t('LAB_ORDER.TABLE.DATE_TIME_ORDER'), class: 'text-nowrap' },
        { id: 'labOrderNumber', disablePadding: false, label: t('LAB_ORDER.TABLE.LAB_ORDER_NUMBER'), class: 'text-nowrap' },
        { id: 'labCompanyName', disablePadding: false, label: t('LAB_ORDER.TABLE.COMPANY'), class: 'text-nowrap' },
        { id: 'dateExport', disablePadding: false, label: t('LAB_ORDER.TABLE.DATE_EXPORT'), class: 'text-nowrap' },
        { id: 'dateReceive', disablePadding: false, label: t('LAB_ORDER.TABLE.DATE_RECEIVE'), class: 'text-nowrap' },
        { id: 'dateReply', disablePadding: false, label: t('LAB_ORDER.TABLE.DATE_REPLY'), class: 'text-nowrap' },
        { id: 'patientId', disablePadding: false, label: t('APPLICATION.TABLE.CELL.CN_NUMBER'), class: 'text-nowrap' },
        { id: 'patientId', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.BRANCH_ID'), class: 'text-nowrap' },
        { id: 'patientId', disablePadding: false, label: t('PATIENT_MANAGEMENT.TABLE.CELL.SN_CODE'), class: 'text-nowrap' },
        { id: 'patientFullName', disablePadding: false, label: t('LAB_ORDER.TABLE.PATIENT_NAME'), class: 'text-nowrap' },
        { id: 'labOrderStatus', disablePadding: false, label: t('LAB_ORDER.TABLE.STATUS'), class: 'text-nowrap' },
        { id: 'dateBill', disablePadding: false, label: t('LAB_ORDER.TABLE.DATE_BILL'), class: 'text-nowrap' },
        { id: 'labOrderPrice', disablePadding: false, label: t('LAB_ORDER.TABLE.PRICE'), class: 'text-nowrap' },
        { id: 'staffBy', disablePadding: false, label: t('LAB_ORDER.TABLE.SAVED_BY'), class: 'text-nowrap', align: 'center' },
    ]
    if (!snType) {
        desktopHeadCells.splice(9, 1)
    }
    const tableHeadCells = [
        { id: 'action', disablePadding: false, label: t('LAB_ORDER.TABLE.ACTION'), class: 'text-nowrap' },
    ]
    const headCells = width >= 1200 ? [...desktopHeadCells, ...tableHeadCells] : [...tableHeadCells]
    const arrayLabOrderStatus: string[] = ['ORDER_LAB', 'SEND_LAB', 'RECEIVE_LAB', 'RECEIVE_BILL', 'REPLY_LAB', 'CANCEL_LAB', 'CLOSE_LAB']

    const getLabelLabOrderStatus = (status: string) => {
        let label: any = ''
        if (status === 'ORDER_LAB') label = t('LAB_ORDER.LAB_STATUS.ORDER_LAB')
        else if (status === 'SEND_LAB') label = t('LAB_ORDER.LAB_STATUS.SEND_LAB')
        else if (status === 'RECEIVE_LAB') label = t('LAB_ORDER.LAB_STATUS.RECEIVE_LAB')
        else if (status === 'RECEIVE_BILL') label = t('LAB_ORDER.LAB_STATUS.RECEIVE_BILL')
        else if (status === 'REPLY_LAB') label = t('LAB_ORDER.LAB_STATUS.REPLY_LAB')
        else if (status === 'CANCEL_LAB') label = t('LAB_ORDER.LAB_STATUS.CANCEL_LAB')
        else if (status === 'CLOSE_LAB') label = t('LAB_ORDER.LAB_STATUS.CLOSE_LAB')
        return label
    }

    const renderData = (objData: LabOrderListInterface, no: number) => {
        no = page * pageLimit - pageLimit + no + 1


        const { labOrderId: id, dateOrder, timeOrder, labOrderNumber: lbNo, labCompanyName, dateExport, dateImport, dateBill, dateReply, cnNumber, branchCnNumber, snCode, patientFullName, labOrderPrice, labOrderStatus, updatedBy, updatedAt } = objData
        const objRenderData = {
            key: String(no),
            id: String(id),
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: no },
                { option: 'TEXT', align: 'center', label: dateTimeToView(`${dateOrder} ${timeOrder}`, false) || '', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'center', label: lbNo || '', class: 'text-nowrap-ellipsis ' },
                { option: 'TEXT', align: 'left', label: labCompanyName || '', class: 'text-nowrap-ellipsis labCompanyName' },
                { option: 'TEXT', align: 'center', label: dateToView(dateExport, false) || '-', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'center', label: dateToView(dateImport, false) || '-', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'center', label: dateReply ? dateToView(dateReply, false) : '-', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'left', label: cnNumber || '-', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'left', label: branchCnNumber || '-', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'left', label: snCode || '-', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'left', label: patientFullName || '', class: 'text-nowrap-ellipsis patientFullName' },
                { option: 'TEXT', align: 'center', label: getLabelLabOrderStatus(labOrderStatus) || '', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'center', label: dateBill !== null ? dateToView(dateBill, false) : '-', class: 'text-nowrap-ellipsis' },
                { option: 'TEXT', align: 'right', label: numberToCurrencyFormat(labOrderPrice || 0), class: 'text-nowrap-ellipsis' },
                { option: 'UPDATE_BY', align: 'center', label: { updatedBy, updatedAt } },

                {
                    option: 'ACTION',
                    align: 'center',
                    label: 'action',
                    values: [
                        {
                            option: 'COMPONANT', label: (
                                <DropdownItem onClick={() => onCancel(objData)} disabled={permissions.isUpdate.disabled || labOrderStatus === 'CANCEL_LAB'}>
                                    <span className="ml-2 align-text-bottom">{t('LAB_ORDER.LAB_STATUS.CANCEL_LAB')}</span>
                                </DropdownItem>
                            )
                        },
                        {
                            option: 'COMPONANT', label: (
                                <DropdownItem onClick={() => { return }} disabled={true}>
                                    <span className="ml-2 align-text-bottom">{t('LAB_ORDER.LAB_STATUS.REPLY_LAB')}</span>
                                </DropdownItem>
                            )
                        },
                        { option: 'DIVIDER', label: '', disabled: false },

                        { option: 'DIVIDER', label: '', disabled: false },
                        { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled || labOrderStatus !== 'ORDER_LAB' },
                        { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled || labOrderStatus !== 'ORDER_LAB' }
                    ]
                },
            ]
        }
        if (!snType) {
            objRenderData.columns.splice(9, 1)
        }
        return <TableRowCommon rowLink={true} {...objRenderData} onClick={(e, val) => onView(e, val)} onedit={() => onEdit(objData)} ondelete={() => onDelete(objData)} />
    }

    const renderDataSlim = (objData: LabOrderListInterface, no: number) => {
        no = page * pageLimit - pageLimit + no + 1
        const { dateOrder, timeOrder, labOrderNumber: lbNo, labCompanyName, dateExport, dateImport, cnNumber, branchCnNumber, snCode, patientFullName, labOrderPrice, labOrderStatus, updatedBy, dateReply } = objData
        return (
            <UseStyled.TBody>
                <div className={'d-flex align-items-center position-relative'}>
                    <UseStyled.Cell>
                        <Grid container sx={{ pl: '24px', mx: 0 }}>
                            <div className="position-absolute w-auto px-0" style={{ marginLeft: '-1.5rem' }}>
                                {no}
                            </div>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.DATE_TIME_ORDER')}</Grid>
                            <Grid item xs={6} md={9} lg={6} xl={3} sx={{ px: '4px' }}>{dateTimeToView(`${dateOrder} ${timeOrder}`)}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.LAB_ORDER_NUMBER')}</Grid>
                            <Grid item xs={6} md={9} lg={6} xl={3} sx={{ px: '4px' }}>{lbNo}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.COMPANY')}</Grid>
                            <Grid item xs={6} md={9} lg={6} xl={9} sx={{ px: '4px' }}>{labCompanyName}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.DATE_EXPORT')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>{dateToView(dateExport) || '-'}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.DATE_RECEIVE')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>{dateToView(dateImport) || '-'}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.DATE_REPLY')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>{dateReply ? dateToView(dateReply) : '-'}</Grid>

                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('APPLICATION.TABLE.CELL.CN_NUMBER')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>{cnNumber || '-'}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('PATIENT_MANAGEMENT.TABLE.CELL.BRANCH_ID')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>{branchCnNumber || '-'}</Grid>
                            {snType && (
                                <>
                                    <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>
                                        {t('PATIENT_MANAGEMENT.TABLE.CELL.SN_CODE')}
                                    </Grid>
                                    <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>
                                        {snCode || '-'}
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.PATIENT_NAME')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>{patientFullName || ''}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.STATUS')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>{getLabelLabOrderStatus(labOrderStatus) || ''}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.PRICE')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} sx={{ px: '4px' }}>{numberToCurrencyFormat(labOrderPrice || 0)}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="fontweght-bold" sx={{ px: '4px' }}>{t('LAB_ORDER.TABLE.SAVED_BY')}</Grid>
                            <Grid item xs={6} md={3} lg={6} xl={3} className="text-nowrap-ellipsis" sx={{ px: '4px' }}>{updatedBy}</Grid>
                        </Grid>
                    </UseStyled.Cell>
                </div>
            </UseStyled.TBody>
        )
    }
    const renderImage = (img: any) => {
        if (!_.isEmpty(img) && !_.isEmpty(img.filePath) && !_.isEmpty(img.fileName)) return `${fileUrl}/${img.filePath}/${img.fileName}`
        else return imgs.defaultAvatar
    }
    const onViewHistory = () => {
        dispatch(showModal())
        setIsShowPopupDetail(true)
    }
    return (
        <div>
            <HeaderLabel text={t('LAB_ORDER.TITLE_HISTORY_LAB')} noAction={true} goBack={routeName.labOrder} navigator={{ previousTo: routeName.labOrder, previousTitle: t('LAB_ORDER.HEADER'), currentTitle: t('LAB_ORDER.HISTORY_LAB') }} />
            <div className="px-3">
                <div className="col-12 col-xl-6 mb-3">
                    <SearchPatients
                        status="ACTIVE"
                        callbackPatientId={loadPatient}
                        patient={patient}
                        patientId={patientId}

                        onClear={() => {
                            setPatientId(0)
                            setPatient('')
                            setLabOrders([])
                            history.replace(window.location.pathname, {
                            })
                        }}
                    />
                </div>
                <TreatmentsUseStyled.CardProfile className="d-flex px-3" onClick={onViewHistory}>
                    <BtnProfile profile={renderImage(patient.profile)} branchCnNumber={patient.branchCnNumber} firstName={patient.firstname} lastName={patient.lastname} firstnameEn={patient.firstnameEn} lastnameEn={patient.lastnameEn} displayName={patient.displayName} rankId={patient.rankId} lastVisit={patient.updatedAt} />
                </TreatmentsUseStyled.CardProfile>
                <PopupPatientDetail type='lab-order' isNotShownPhone={user.hasShowPhoneNumber === 'UNPERMISS'} profile={renderImage(patient.profile)} patient={patient} showPopup={isShowPopupDetail} onClose={() => setIsShowPopupDetail(false)} />
                <div className='table-payment custom-scroll my-3' >
                    <Grid container sx={{ mx: 0, alignItems: 'center' }}>
                        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: '8px', px: 0, pr: { sm: '4px' } }}>
                            <InputTextSearch keyInput={'search'} label={t('LAB_ORDER.LIST.SEARCH')} value={filterSearch}
                                onchange={(event) => {
                                    setFilterSearch(event.target.value)
                                    setPage(1)
                                }}
                                onClear={(event) => {
                                    setFilterSearch('')
                                    setPage(1)
                                }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: '8px', px: 0, pl: { sm: '4px' }, pr: { md: '4px' } }}>
                            <FilterSelect
                                fullWidth
                                renderValue={() => `${t('LAB_ORDER.LIST.COMPANY')}: ${_.find(labCompanyOptions, (labCompany: any) => labCompany.labCompanyId === filterLabCompany)?.labCompanyName || t('ALL')}`}
                                label={t('LAB_ORDER.LIST.COMPANY')}
                                selectId="select-status"
                                value={filterLabCompany}
                                onchange={(event) => {
                                    setFilterLabCompany(event.target.value)
                                    setPage(1)
                                }}
                                labelId="label-status"
                                options={[
                                    <MenuItem key={'all'} value={0}>
                                        {t('ALL')}
                                    </MenuItem>,
                                    _.map(labCompanyOptions, (labCompany: any, i: number) => (
                                        <MenuItem key={i} value={labCompany.labCompanyId}>
                                            {labCompany.labCompanyName}
                                        </MenuItem>
                                    ))
                                ]}
                                formControlStyle={{ maxWidth: 'unset !important' }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: '8px', px: 0, pr: { sm: '4px', md: 0, lg: '4px' }, pl: { md: '4px' } }}>
                            <FilterSelect
                                fullWidth-
                                renderValue={() => `${t('LAB_ORDER.LIST.STATUS')}: ${getLabelLabOrderStatus(_.find(arrayLabOrderStatus, (statusLab: any) => statusLab === filterLabOrderStatus) || '') || t('ALL')
                                    } `}
                                label={t('LAB_ORDER.LIST.STATUS')}
                                selectId="select-status"
                                value={filterLabOrderStatus}
                                onchange={(event) => {
                                    setFilterLabOrderStatus(event.target.value)
                                    setPage(1)
                                }}
                                labelId="label-status"
                                options={[
                                    <MenuItem key={'all'} value={0}>
                                        {t('ALL')}
                                    </MenuItem>,
                                    _.map(arrayLabOrderStatus, (statusLab: any, i: number) => (
                                        <MenuItem key={i} value={statusLab}>
                                            {getLabelLabOrderStatus(statusLab)}
                                        </MenuItem>
                                    ))
                                ]}
                                formControlStyle={{ maxWidth: 'unset !important' }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: '8px', px: 0, pl: { sm: '4px', md: 0, lg: '4px' } }}>
                            <InputSecondNewRangePicker
                                value={filterRangeDate || emptyRangePicker}
                                inputHeight={32}
                                onchange={(val) => {
                                    setPage(1)
                                    if (_.isEmpty(val)) setFilterRangeDate(emptyRangePicker)
                                    else setFilterRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')])
                                }}
                                allowClear
                                onClear={() => {
                                    setPage(1)
                                    setFilterRangeDate(emptyRangePicker)
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ mb: '8px', ml: 'auto', px: 0 }}>
                            <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={onCreate} textButton={t('LAB_ORDER.LIST.ADD_LAB')} className="w-auto ml-auto d-flex mt-auto" />
                        </Grid>
                    </Grid>
                    {width >= 1441 ? (
                        <TableCustom
                            page={page}
                            pageLimit={pageLimit}
                            sortType={sortType}
                            sortBy={sortBy}
                            rowCount={rowCount}
                            onSort={(sortByVal: string, sortTypeVal: string) => {
                                setSortType(sortTypeVal)
                                setSortBy(sortByVal)
                            }}
                            setPageLimit={setPageLimit}
                            setPage={setPage}
                            headCells={headCells}
                            rowsData={_.map(labOrders, renderData)}
                        />
                    ) : (
                        <>
                            <UseStyled.THead>
                                <UseStyled.Cell>{t('#')}</UseStyled.Cell>
                            </UseStyled.THead>
                            <div className={'treatment-body'}>
                                {_.map(labOrders, renderDataSlim)}
                                <div className={'mt-2 '}>
                                    <DefaultPagination count={rowCount} allRow={rowCount} pageLimit={pageLimit} page={page} setPage={setPage} />
                                </div>
                            </div>
                        </>
                    )}

                </div>
                <ViewLabOrder labOrderId={labOrderId} showPopup={isViewDetail} onClose={onClose} />
                <FormLabOrder showForm={isForm} labOrderId={labOrderId} patient={patient} patientId={patientId} onReset={onReset} handleSuccess={loadData} />
                <CancelLabOrder isShow={popupCancel} labOrderNumber={labOrderNumber} id={labOrderId} onSuccess={loadData} onReset={() => setPopupCancel(false)} />
            </div>
        </div>
    )
}
